import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Medal, CalendarDays, MapPin, Navigation, Loader2, Home, ClipboardList } from "lucide-react";
import { SiteLogo } from "@/components/SiteLogo";
import { BrandText } from "@/components/BrandText";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AthleteFormDialog from "@/components/AthleteFormDialog";

const discLabel = { artistica: "Ginnastica Artistica", ritmica: "Ginnastica Ritmica" };

export default function EventRegister() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("ritmica");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/events").then((r) => {
      const ev = r.data.find((e) => e.id === eventId);
      setEvent(ev || null);
      if (ev) setTab(ev.discipline || "ritmica");
    }).finally(() => setLoading(false));
  }, [eventId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Caricamento…</div>;

  return (
    <div className="min-h-screen bg-background text-foreground noise-bg">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
            <SiteLogo className="w-9 h-9" />
            <div className="flex flex-col leading-tight">
              <BrandText />
            </div>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-gold transition-colors inline-flex items-center gap-1.5"><Home className="w-4 h-4" /> Home</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {!event ? (
          <div className="text-center py-24">
            <p className="text-muted-foreground">Evento non trovato o non più disponibile.</p>
            <Link to="/" className="text-gold hover:underline mt-4 inline-block">Torna alla home</Link>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="overline text-gold uppercase text-xs tracking-[0.25em]">Iscrizione evento</span>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-3">{event.title}</h1>
            <div className="flex flex-wrap items-center gap-5 mt-4 text-muted-foreground">
              <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-gold" />{new Date(event.date).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</span>
              {event.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-gold" />{event.location}</span>}
            </div>
            {event.description && <p className="text-muted-foreground mt-5 leading-relaxed">{event.description}</p>}

            {event.location && (
              <div className="mt-6">
                <iframe title="mappa" src={`https://maps.google.com/maps?q=${encodeURIComponent(event.location)}&z=14&output=embed`} className="w-full h-52 rounded-xl border border-white/10 grayscale-[0.3]" loading="lazy" />
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm text-gold hover:text-[#FDE047]"><Navigation className="w-3.5 h-3.5" /> Apri in Google Maps</a>
              </div>
            )}

            <div className="mt-10 rounded-2xl border border-gold/20 bg-gradient-to-b from-gold/5 to-transparent p-8">
              <h2 className="font-display text-2xl">Iscriviti a questo evento</h2>
              <p className="text-muted-foreground text-sm mt-1">Scegli la disciplina e compila il modulo di iscrizione.</p>
              <Tabs value={tab} onValueChange={setTab} className="mt-5">
                <TabsList className="bg-[#0F172A] border border-white/10 h-auto p-1">
                  <TabsTrigger data-testid="ev-tab-ritmica" value="ritmica" className="data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-4 py-2">Ritmica</TabsTrigger>
                  <TabsTrigger data-testid="ev-tab-artistica" value="artistica" className="data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-4 py-2">Artistica</TabsTrigger>
                </TabsList>
              </Tabs>
              <Button data-testid="ev-register-btn" onClick={() => setOpen(true)} className="mt-6 bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
                <ClipboardList className="w-4 h-4 mr-1.5" /> Compila iscrizione — {discLabel[tab]}
              </Button>
            </div>
          </motion.div>
        )}
      </main>

      {event && (
        <AthleteFormDialog
          open={open}
          onOpenChange={setOpen}
          discipline={tab}
          events={[event]}
          editing={null}
          defaultEventId={event.id}
          onSaved={() => {}}
        />
      )}
    </div>
  );
}
